/**
 * Sysadmin Audit → Chain Integrity (Wave 3 §10.2 #7 / spec §3.6).
 *
 * On-demand SHA hash-chain verification. Wires the existing W1a-26
 * endpoint at /admin/platform/audit-chain/verify. Two scopes:
 *   - platform: walks the platform_audit_logs chain
 *   - tenant:   walks audit_logs filtered to one organizationId
 *
 * Verify is expensive (it reads every row in the partition) so we
 * never auto-poll — operator clicks "Verify" and we fire one request.
 * Each verify also writes a row to platform_audit_logs (the W1a-26
 * endpoint emits PLATFORM_AUDIT_VERIFIED), so consecutive runs will
 * see rowsVerified increment by 1 on the platform chain.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Link2, ShieldCheck, AlertTriangle, Play, CheckCircle2, XCircle,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import { useChainVerify, type ChainScope } from '@/lib/use-sysadmin-audit';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/audit/integrity')({
  component: AuditIntegrityPage,
});

function AuditIntegrityPage() {
  const [scope, setScope] = useState<ChainScope>('platform');
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reasonOpen, setReasonOpen] = useState(false);

  // Chain-verify is a tier-1 step-up mutation now (it writes a
  // PLATFORM_AUDIT_VERIFIED row + requires a fresh-TOTP step-up reason). The
  // operator clicks "Run verification" → audit-reason dialog → mutate.
  const verify = useChainVerify();
  const data = verify.data;
  const error = verify.error;
  const isFetching = verify.isPending;
  const showResult = verify.isPending || !!data || !!error;

  const canRun = scope === 'platform' || (scope === 'tenant' && tenantId);

  const run = () => {
    if (!canRun) return;
    setReasonOpen(true);
  };

  const handleVerify = (stepUpReason: string) => {
    verify.mutate(
      {
        input: {
          scope,
          tenantId: scope === 'tenant' ? tenantId || undefined : undefined,
          from: from || undefined,
          to: to || undefined,
        },
        stepUpReason,
      },
      {
        onSuccess: () => setReasonOpen(false),
        onError: () => setReasonOpen(false),
      },
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Link2 className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Chain Integrity</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">§3.6 + W1a-26</Badge>
            <Badge variant="secondary" className="text-xs">v1</Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Verify chain</CardTitle>
          <CardDescription>
            Walks the SHA hash chain row-by-row, verifying that each
            row's <code>prev_hash</code> matches the previous row's
            <code> hash</code>. A break anywhere = tampering or
            corruption. Each verify run writes a
            <code> PLATFORM_AUDIT_VERIFIED</code> row to the platform
            chain (so consecutive runs see the row count increment).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="v-scope" className="text-xs">Scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as ChainScope)}>
                <SelectTrigger id="v-scope" className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platform">Platform chain</SelectItem>
                  <SelectItem value="tenant">Tenant chain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === 'tenant' && (
              <div className="space-y-1.5">
                <Label htmlFor="v-tenant" className="text-xs">Tenant ID</Label>
                <Input
                  id="v-tenant"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="UUID"
                  className="font-mono text-xs h-9"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="v-from" className="text-xs">From (optional ISO)</Label>
              <Input
                id="v-from"
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(toIsoZ(e.target.value))}
                className="text-xs h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-to" className="text-xs">To (optional ISO)</Label>
              <Input
                id="v-to"
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(toIsoZ(e.target.value))}
                className="text-xs h-9"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={run}
              disabled={!canRun || isFetching}
            >
              <Play className="h-3.5 w-3.5 mr-1" />
              {isFetching ? 'Verifying…' : 'Run verification'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      {showResult && (
        <Card
          className={cn(
            data && (data.ok ? 'border-green-500/30' : 'border-destructive/40'),
            error && 'border-destructive/40',
          )}
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {isFetching ? (
                <ShieldCheck className="h-4 w-4 animate-pulse" />
              ) : data?.ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : data && !data.ok ? (
                <XCircle className="h-4 w-4 text-destructive" />
              ) : error ? (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              ) : null}
              Result
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {isFetching && !data && (
              <div className="text-muted-foreground animate-pulse">
                Walking the chain…
              </div>
            )}
            {error && (
              <div className="text-destructive">{error.message}</div>
            )}
            {data && (
              <>
                <Row label="Scope" value={data.scope} />
                {data.tenantId && <Row label="Tenant" value={data.tenantId} mono />}
                <div className="flex items-baseline gap-3 text-sm">
                  <span className="text-muted-foreground w-32 shrink-0">Result</span>
                  <StatusPill
                    status={data.ok ? 'healthy' : 'destructive'}
                    icon={data.ok ? CheckCircle2 : XCircle}
                  >
                    {data.ok ? 'OK — chain intact' : 'BROKEN — first mismatch listed below'}
                  </StatusPill>
                </div>
                <Row label="Rows verified" value={String(data.rowsVerified)} />
                {data.firstMismatch && (
                  <div className="rounded border border-destructive/30 bg-destructive/5 p-3 mt-2 text-xs space-y-1">
                    <div className="font-semibold text-destructive">First mismatch</div>
                    <Row label="Row id" value={data.firstMismatch.id} mono />
                    <Row label="Reason" value={data.firstMismatch.reason} />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {reasonOpen && (
        <StepUpReasonDialog
          open
          onOpenChange={(o) => !o && setReasonOpen(false)}
          title="Audit reason for chain verification"
          description={`Tier-1 step-up. Verifying the ${scope} hash chain writes a PLATFORM_AUDIT_VERIFIED record. State the audit reason.`}
          submitLabel={verify.isPending ? 'Verifying…' : 'Run verification'}
          submitting={verify.isPending}
          onConfirm={handleVerify}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className={cn('text-foreground', mono && 'font-mono text-xs break-all')}>
        {value}
      </span>
    </div>
  );
}

// `<input type="datetime-local">` returns "YYYY-MM-DDTHH:mm" without
// seconds or zone; the API's z.string().datetime() needs the Z form.
function toIsoZ(local: string): string {
  if (!local) return '';
  // Keep an empty string when cleared. Otherwise append :00.000Z if missing.
  if (/Z$/.test(local)) return local;
  if (/:\d{2}$/.test(local)) return `${local}.000Z`;
  return `${local}:00.000Z`;
}
